import { jsPDF } from "jspdf";
import moment from "moment";
import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  Col,
  Collapse,
  Container,
  PopoverBody,
  Row,
  Table,
  UncontrolledPopover,
} from "reactstrap";
import Swal from "sweetalert2";
import ButtonBack from "../../Components/ButtonBack";
import InvoicePaymentStatusBadge from "../../Components/Invoices/InvoicePaymentStatusBadge";
import Loading from "../../Components/Loading";
import { PrimaryLogo } from "../../Components/Logo/PrimaryLogo";
import CommissionDetailTable from "../../Components/Tables/CommissionDetailTable/CommissionDetailTable";
import { DASHBOARD_USER_ROLES, singularRoleLabels } from "../../constants";
import { useCurrentUser } from "../../slices/auth/hooks";
import {
  getInvoiceById,
  getInvoicesOfMonth,
  payInvoiceNew,
  payNextInvoice,
  refundInvoice
} from "../../slices/general/thunk";
import {
  capitalizeFirstLetter,
  formatDateForInvoice,
  formatInvoiceDueDateFromEffectiveDate,
  formatTimeForInvoice,
  getAbbreviatedUsState,
  parsedInvoiceAmount
} from "../../utils/utils";
const InvoiceDetails = () => {
  const { id, employerId } = useParams();

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const locations = useLocation();
  const currentUser = useCurrentUser();

  const isAdmin = currentUser.role === DASHBOARD_USER_ROLES.ADMIN;

  const [loading, setLoading] = useState(false);

  const [loadingPay, setLoadingPay] = useState(false);

  const [loadingRefund, setLoadingRefund] = useState(false);

  const [invoice, setInvoice] = useState([]);

  const renderPayInvoiceButton = !invoice.paid && !invoice.paymentProcessing;
  const showInvocePaid = invoice.paid || invoice.paymentProcessing;

  const [isCollapseOpen, setIsCollapseOpen] = useState([]);

  const commissions = invoice?.commissions || [];

  const printInvoice = () => {
    window.print();
  };


  const downloadInvoice = () => {
    const doc = new jsPDF();
    let currentY = 20; // Initial position for the document

    const date = invoice.effectiveForDate;
    const paymentDate = invoice.date;
    const invoiceMonthLabel = moment.utc(date).format("MMMM");
    const invoiceYear = moment(date).format("YYYY");
    const totalAmountFormatted = (invoice.totalAmount / 100).toFixed(2);
    const resolveToName = // If it has no firstname and last name, use company name
      invoice.dashboardUserInfo?.firstName && invoice.dashboardUserInfo?.lastName
        ? `${invoice.dashboardUserInfo?.firstName} ${invoice.dashboardUserInfo?.lastName}`
        : invoice.dashboardUserInfo?.companyName;

    // Render the header
    const renderHeader = () => {
      doc.setFontSize(20);
      doc.text(`Invoice - ${invoiceMonthLabel} - ${invoiceYear}`, 20, currentY);
      currentY += 10;

      doc.setFontSize(16);
      doc.text("BaxRx", 20, currentY);
      currentY += 10;

      doc.setFontSize(12);

      doc.text("St. Michael, MN", 20, currentY);

      doc.setFontSize(10);
      doc.text(
        `To: ${resolveToName}`
        , 130, currentY);
      currentY += 5;
      // Put email below
      doc.text(
        `${invoice.dashboardUserInfo?.email}`
        , 130, currentY);
      // Phone number
      currentY += 5;
      doc.text(
        `${invoice.dashboardUserInfo?.phoneNumber || ''}`
        , 130, currentY);
      // Full address
      currentY += 5;
      doc.text(
        `${invoice.dashboardUserInfo?.fullAddress || ''}`
        , 130, currentY);

      // Go back to original Y position
      currentY -= 15;

      currentY += 5;

      doc.setFontSize(12);
      doc.text("612-964-1898", 20, currentY);
      currentY += 5;

      doc.setFontSize(12);
      doc.text("Merchant Org ID: 234927", 20, currentY);
      currentY += 5;

      doc.setFontSize(12);
      doc.text("Merchant ID: 346601", 20, currentY);
      currentY += 10;

      doc.setFontSize(10);
      doc.text(
        `Date of Transaction: ${moment.utc(paymentDate).format("MM/DD/YYYY")}`
        , 20, currentY);
      currentY += 5;

      if (invoice?.dexTransactionInfo?.transaction_id) {
        doc.text(
          `Transaction ID: ${invoice?.dexTransactionInfo?.transaction_id}`
          , 20, currentY);
      }

      currentY += 10;



      doc.setFontSize(14);
      doc.setTextColor(56, 56, 56);
      doc.text(`Invoice Balance`, 20, currentY);
      doc.text(`Payment Due Date`, 80, currentY);
      doc.text(`Payment Status`, 160, currentY);
      currentY += 10;

      doc.setFontSize(18);
      doc.setTextColor(33, 33, 33);
      doc.text(`$${totalAmountFormatted}`, 20, currentY);
      doc.text(
        formatInvoiceDueDateFromEffectiveDate(invoice.effectiveForDate),
        80,
        currentY
      );
      doc.text(
        `${showInvocePaid
          ? "Paid"
          : invoice.paymentProcessing
            ? "Processing"
            : "Due"
        }`,
        160,
        currentY
      );
      currentY += 10;

      if (showInvocePaid) {
        doc.setFillColor(204, 255, 204);
        doc.rect(20, currentY, 170, 10, "F");
        doc.setFontSize(12);
        doc.setTextColor(0, 0, 0);
        doc.text(
          `Payment Submitted on ${moment.utc(paymentDate).format(
            "MM/DD/YYYY"
          )}`,
          25,
          currentY + 7
        );
      } else if (invoice.paymentProcessing) {
        doc.setFillColor(255, 255, 204);
        doc.rect(20, currentY, 170, 10, "F");
        doc.setFontSize(12);
        doc.setTextColor(0, 0, 0);
        doc.text(`Payment Processing`, 25, currentY + 7);
      } else {
        doc.setFillColor(255, 204, 204);
        doc.rect(20, currentY, 170, 10, "F");
        doc.setFontSize(12);
        doc.setTextColor(0, 0, 0);
        doc.text(
          `Due on ${formatInvoiceDueDateFromEffectiveDate(
            invoice?.effectiveForDate
          )}`,
          25,
          currentY + 7
        );
      }
      currentY += 20; // Move down after status box

      // Show transaction ID if available
      // if (invoice.dexTransactionInfo?.transaction_id) {
      //   doc.setFontSize(12);
      //   doc.setTextColor(0, 0, 0);
      //   doc.text(
      //     `Transaction ID: ${invoice.dexTransactionInfo.transaction_id}`,
      //     20,
      //     currentY
      //   );
      //   currentY += 10;
      // }
    };

    // Render invoice items
    // Render invoice items
    const renderItems = () => {
      doc.setFontSize(16);
      doc.setTextColor(0, 0, 0);
      doc.text("INVOICE DETAILS", 20, currentY);
      currentY += 10;

      // Add Total Employees
      const totalEmployees = invoice.items.length;
      doc.setFontSize(14);
      doc.setTextColor(56, 56, 56);
      doc.text(`Total Employees: ${totalEmployees}`, 20, currentY);
      currentY += 10;

      const startingY = currentY;
      const lineHeight = 10; // Line height for items
      const pageHeight = doc.internal.pageSize.height - 20; // Leave space for footer
      let itemY = startingY;

      doc.setFontSize(12);
      doc.setTextColor(0, 0, 0);
      doc.text("DETAILS", 20, itemY);
      doc.text("TIER", 150, itemY, null, null, "center"); // Add TIER column header
      doc.text("AMOUNT", 180, itemY, null, null, "right");
      itemY += 10;

      doc.setDrawColor(0);
      doc.line(20, itemY, 190, itemY);
      itemY += 10;

      // Loop through invoice items
      invoice.items.forEach((item, index) => {
        // Check if we need a new page
        if (itemY + lineHeight * 2 > pageHeight) {
          doc.addPage(); // Add a new page
          itemY = 20; // Reset starting position for the new page

          // Re-add the header for the new page
          doc.setFontSize(16);
          doc.text("DETAILS", 20, itemY);
          doc.text("TIER", 150, itemY, null, null, "center"); // Add TIER column header
          doc.text("AMOUNT", 180, itemY, null, null, "right");
          itemY += 10;
          doc.line(20, itemY, 190, itemY);
          itemY += 10;

          // Reset font size and color for item details
          doc.setFontSize(12);
          doc.setTextColor(0, 0, 0);
        }

        const itemType = item.type
          ? capitalizeFirstLetter(item.type)
          : "Employee";

        // Determine tier
        const isFamilyTier = item.metadata?.employee?.tier === 'F' ||
          item.metadata?.employee?.isFamily === true ||
          item.metadata?.pricingTier === 'family';
        const tierValue = isFamilyTier ? 'F' : 'E';

        // Add item details
        doc.text(`${item.description}`, 20, itemY);
        doc.text(tierValue, 150, itemY, null, null, "center"); // Add tier value
        doc.text(
          `$${(item.amount / 100).toFixed(2)}`,
          180,
          itemY,
          null,
          null,
          "right"
        );
        itemY += 5;

        // Add item type
        doc.setFontSize(10);
        doc.setTextColor(100, 100, 100);
        doc.text(itemType, 20, itemY);
        itemY += lineHeight;

        // Reset font size and color for the next items
        doc.setFontSize(12);
        doc.setTextColor(0, 0, 0);
      });

      currentY = itemY; // Update currentY to the last item position
    };

    // Execute rendering functions
    renderHeader();
    renderItems();

    // Save the PDF
    const fileName = `BaxRx - Invoice - ${invoiceMonthLabel} ${invoiceYear}.pdf`;
    doc.save(fileName);
  };

  const isCurrentMonthPage = location.pathname.includes("current-month");

  const fetchInvoiceById = async () => {
    try {
      setLoading(true);
      const actionResult = await dispatch(
        getInvoiceById({
          userId: isAdmin ? undefined : currentUser.id,
          invoiceId: id,
          isAdmin: isAdmin,
        })
      );
      const res = actionResult.payload;

      console.log("RESPONSE INVOICE BY ID", res);

      if (res.error) {
        Swal.fire({
          title: "Error",
          text: res.error,
          icon: "error",
        });
        navigate("/billing");
      } else {
        setInvoice(res);
      }
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const fetchInvoicesOfMonth = async () => {
    try {
      setLoading(true);
      const res = await dispatch(getInvoicesOfMonth({ userId: employerId }));
      if (res.payload.error) {
        setInvoice(null);
        navigate("/billing");
      } else {
        setInvoice(res.payload);
      }
      setLoading(false);
    } catch (error) {
      setLoading(true);
      console.log(error);
      setLoading(false);
    }
  };

  const payInvoiceById = async () => {
    try {
      console.log("paying invoiceby id", invoice);
      setLoadingPay(true);
      const res = await dispatch(
        payInvoiceNew({
          userId: invoice?.dashboardUserId,
          invoiceId: id,
        })
      );
      console.log(res);
      if (res.payload.error) {
        Swal.fire({
          title: "Error",
          text: res.payload.error,
          icon: "error",
        });
      } else {
        // Show message about payment processing
        Swal.fire({
          title: "Success",
          text: "Invoice payment authorized and processing. It may take a few minutes to reflect in your account.",
          icon: "success",
        });
        fetchInvoiceById();
      }
      setLoadingPay(false);
    } catch (error) {
      setLoadingPay(true);
      Swal.fire({
        title: "Error",
        text: error,
        icon: "error",
      });
      console.log(error);
      setLoadingPay(false);
    }
  };

  const payInvoiceOfMonth = async () => {
    try {
      setLoadingPay(true);
      const res = await dispatch(
        payNextInvoice({
          userId: currentUser.id,
        })
      );
      console.log(res);
      if (res.payload.error) {
        Swal.fire({
          title: "Error",
          text: res.payload.error,
          icon: "error",
        });
      } else {
        // Show message about payment processing
        Swal.fire({
          title: "Success",
          text: "Invoice payment authorized and processing. It may take a few minutes to reflect in your account.",
          icon: "success",
        });
        navigate("/billing");
      }
      setLoadingPay(false);
    } catch (error) {
      setLoadingPay(true);
      Swal.fire({
        title: "Error",
        text: error,
        icon: "error",
      });
      console.log(error);
      setLoadingPay(false);
    }
  };

  const handleRefundInvoice = async () => {
    // Calculate max refundable amount
    const totalRefunded = invoice.totalRefundedAmount || 0;
    const maxRefundable = invoice.totalAmount - totalRefunded;
    const maxRefundableDollars = (maxRefundable / 100).toFixed(2);

    // First step: Ask for refund type and amount
    const refundTypeResult = await Swal.fire({
      title: "Issue Refund",
      html: `
        <div style="text-align: left;">
          <p><strong>Invoice Total:</strong> $${parsedInvoiceAmount(invoice.totalAmount)}</p>
          ${totalRefunded > 0 ? `<p><strong>Already Refunded:</strong> $${parsedInvoiceAmount(totalRefunded)}</p>` : ''}
          <p><strong>Max Refundable:</strong> $${maxRefundableDollars}</p>
          <hr/>
          <div class="mb-3">
            <label class="form-label">Refund Type:</label>
            <select id="refund-type" class="swal2-select" style="width: 100%; padding: 8px; border-radius: 4px; border: 1px solid #ccc;">
              <option value="full">Full Refund ($${maxRefundableDollars})</option>
              <option value="partial">Partial Refund</option>
            </select>
          </div>
          <div id="partial-amount-container" style="display: none;">
            <label class="form-label">Amount to Refund (in dollars):</label>
            <input type="number" id="refund-amount" class="swal2-input" step="0.01" min="0.01" max="${maxRefundableDollars}" placeholder="e.g., 50.00" style="width: 100%;">
          </div>
        </div>
      `,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Continue",
      cancelButtonText: "Cancel",
      didOpen: () => {
        const refundTypeSelect = document.getElementById('refund-type');
        const partialAmountContainer = document.getElementById('partial-amount-container');
        refundTypeSelect.addEventListener('change', (e) => {
          partialAmountContainer.style.display = e.target.value === 'partial' ? 'block' : 'none';
        });
      },
      preConfirm: () => {
        const refundType = document.getElementById('refund-type').value;
        const refundAmountInput = document.getElementById('refund-amount').value;

        if (refundType === 'partial') {
          const refundAmount = parseFloat(refundAmountInput);
          if (!refundAmountInput || isNaN(refundAmount) || refundAmount <= 0) {
            Swal.showValidationMessage('Please enter a valid refund amount');
            return false;
          }
          if (refundAmount > parseFloat(maxRefundableDollars)) {
            Swal.showValidationMessage(`Amount cannot exceed $${maxRefundableDollars}`);
            return false;
          }
          return { type: 'partial', amount: Math.round(refundAmount * 100) };
        }
        return { type: 'full', amount: null };
      },
    });

    if (!refundTypeResult.isConfirmed) return;

    const { type: refundType, amount: refundAmountCents } = refundTypeResult.value;
    const refundDisplayAmount = refundType === 'full'
      ? maxRefundableDollars
      : (refundAmountCents / 100).toFixed(2);

    // Second confirmation with REFUND text
    const secondConfirm = await Swal.fire({
      title: "Final Confirmation",
      html: `
        <p>This action <strong>cannot be undone</strong>.</p>
        <p>Refund Type: <strong>${refundType === 'full' ? 'Full Refund' : 'Partial Refund'}</strong></p>
        <p>The customer will be refunded <strong>$${refundDisplayAmount}</strong>.</p>
        <p>Type <strong>REFUND</strong> to confirm:</p>
      `,
      icon: "warning",
      input: "text",
      inputPlaceholder: "Type REFUND",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Issue Refund",
      cancelButtonText: "Cancel",
      inputValidator: (value) => {
        if (value !== "REFUND") {
          return "Please type REFUND to confirm";
        }
      },
    });

    if (!secondConfirm.isConfirmed) return;

    try {
      setLoadingRefund(true);
      const res = await dispatch(refundInvoice({
        invoiceId: id,
        amount: refundAmountCents
      }));

      if (res.payload.error) {
        Swal.fire({
          title: "Error",
          text: res.payload.error,
          icon: "error",
        });
      } else {
        Swal.fire({
          title: "Success",
          text: refundType === 'full'
            ? "Invoice has been fully refunded."
            : `Partial refund of $${refundDisplayAmount} has been processed.`,
          icon: "success",
        });
        fetchInvoiceById();
      }
    } catch (error) {
      Swal.fire({
        title: "Error",
        text: "An error occurred while processing the refund.",
        icon: "error",
      });
      console.error(error);
    } finally {
      setLoadingRefund(false);
    }
  };

  useEffect(() => {
    if (!isCurrentMonthPage) {
      fetchInvoiceById();
    } else {
      fetchInvoicesOfMonth();
    }
  }, []);

  useEffect(() => {
    if (invoice && invoice.totalAmount === 0) {
      navigate("/billing");
    }
  }, [invoice]);

  const renderNotes = () => {
    <div className="mt-4">
      <div className="alert alert-info">
        <p className="mb-0">
          <span className="fw-semibold">NOTES:</span>
          <span id="note">
            {" "}
            All accounts are to be paid within 7 days from receipt of invoice.
            To be paid by cheque or credit card or direct payment online. If
            account is not paid within 7 days the credits details supplied as
            confirmation of work undertaken will be charged the agreed quoted
            fee noted above.
          </span>
        </p>
      </div>
    </div>;
  };

  const renderPaymentMethod = () => {
    <div className="mt-3">
      <h6 className="text-muted text-uppercase fw-semibold mb-3">
        Payment Details:
      </h6>
      <p className="text-muted mb-1">
        Payment Method:{" "}
        <span className="fw-medium" id="payment-method">
          Mastercard
        </span>
      </p>
      <p className="text-muted mb-1">
        Card Holder:{" "}
        <span className="fw-medium" id="card-holder-name">
          David Nichols
        </span>
      </p>
      <p className="text-muted mb-1">
        Card Number:{" "}
        <span className="fw-medium" id="card-number">
          xxx xxxx xxxx 1234
        </span>
      </p>
      <p className="text-muted">
        Total Amount:{" "}
        <span className="fw-medium" id="">
          $755.96
        </span>
      </p>
    </div>;
  };

  const renderBillingAddress = (invoice) => {
    const dashboardUserInfo = invoice.dashboardUserInfo;

    let nameColumn;

    if (invoice.dashboardUserInfo?.companyName) {
      nameColumn = `${dashboardUserInfo?.companyName}`;
    } else if (dashboardUserInfo?.firstName || dashboardUserInfo?.lastName) {
      nameColumn = `${dashboardUserInfo?.firstName || ""} ${dashboardUserInfo?.lastName || ""
        } `;
    }

    // ADDRESS FORMAT DESIRED:
    // <p className="text-muted mb-1" id="address-details">
    //                         4625 Lange Avenue NE, United States
    //                       </p>
    //                       <p className="text-muted mb-0" id="zip-code">
    //                         <span>St. Michael, MN 55376</span>
    //                       </p>

    const addressLine1 = dashboardUserInfo?.companyAddress;
    const { city, state, zipCode } = dashboardUserInfo || {};

    const composeAddressLine2 = () => {
      let result = "";

      if (city) {
        result += `${city}`;
      }

      if (state) {
        result += `${city ? ", " : ""}${getAbbreviatedUsState(state)}`;
      }

      if (zipCode) {
        result += `${city || state ? " " : ""}${zipCode}`;
      }

      return result.trim();
    };

    const addressLine2 = composeAddressLine2();

    return (
      <Col lg={12}>
        <CardBody className="p-4 border-top border-top-dashed">
          <Row className="g-3">
            <Col sm={3} className="border rounded-4 p-3">
              <p className="text-dark mb-2 text-uppercase fw-normal">
                Company Information
              </p>
              <p className="fw-medium mb-2" id="billing-name">
                {nameColumn}
              </p>
              {/* <p className="text-muted mb-1" id="billing-address-line-1">
                {dashboardUserInfo?.companyAddress}
              </p> */}
              <p className="text-muted mb-1" id="address-details">
                {addressLine1}
              </p>
              {addressLine2 && (
                <p className="text-muted mb-0" id="zip-code">
                  <span>{composeAddressLine2()}</span>
                </p>
              )}

              {/* {dashboardUserInfo?.email && (
                <p className="text-muted mb-1">
                  <span>Email: </span>
                  <span id="billing-phone-no">{dashboardUserInfo?.email}</span>
                </p>
              )} */}
              {/* {dashboardUserInfo?.role && (
                <p className="text-muted mb-0">
                  <span>Type: </span>
                  <span id="billing-tax-no">
                    {capitalizeFirstLetter(dashboardUserInfo.role)}
                  </span>
                </p>
              )} */}
            </Col>
            {/* <Col sm={6}>
              <h6 className="text-muted text-uppercase fw-semibold mb-3">
                Shipping Address
              </h6>
              <p className="fw-medium mb-2" id="shipping-name">
                David Nichols
              </p>
              <p className="text-muted mb-1" id="shipping-address-line-1">
                305 S San Gabriel Blvd
              </p>
              <p className="text-muted mb-1">
                <span>Phone: +</span>
                <span id="shipping-phone-no">(123) 456-7890</span>
              </p>
            </Col> */}
          </Row>
        </CardBody>
      </Col>
    );
  };

  const renderButtonPayNow = (invoice) => {
    if (loading) {
      return null;
    }

    // If it's current-month do not show.
    if (isCurrentMonthPage) {
      return null;
    }

    // Calculate refund status
    const totalRefunded = invoice.totalRefundedAmount || 0;
    const maxRefundable = invoice.totalAmount - totalRefunded;
    const isFullyRefunded = invoice.refunded && !invoice.partiallyRefunded;
    const isPartiallyRefunded = invoice.partiallyRefunded;
    const canRefundMore = maxRefundable > 0;

    if (showInvocePaid) {
      return (
        <div className="d-flex align-items-center gap-2">
          {isFullyRefunded ? (
            <Button color="secondary" className="btn btn-secondary py-1" disabled>
              <i className="ri-refund-2-line align-bottom me-1"></i>
              Fully Refunded
            </Button>
          ) : isPartiallyRefunded ? (
            <>
              <Button color="warning" className="btn btn-warning py-1" disabled>
                <i className="ri-refund-2-line align-bottom me-1"></i>
                Partially Refunded (${parsedInvoiceAmount(totalRefunded)})
              </Button>
              {isAdmin && canRefundMore && (
                <Button
                  color="danger"
                  className="btn btn-soft-danger py-1"
                  onClick={handleRefundInvoice}
                  disabled={loadingRefund}
                >
                  {loadingRefund ? (
                    <i className="ri-loader-4-line animate-spin align-bottom me-1"></i>
                  ) : (
                    <i className="ri-refund-2-line align-bottom me-1"></i>
                  )}
                  Refund More
                </Button>
              )}
            </>
          ) : (
            <>
              <Button color="success" className="btn btn-success py-1" disabled>
                <i className="ri-check-line align-bottom me-1"></i>
                Paid
              </Button>
              {isAdmin && (
                <Button
                  color="danger"
                  className="btn btn-soft-danger py-1"
                  onClick={handleRefundInvoice}
                  disabled={loadingRefund}
                >
                  {loadingRefund ? (
                    <i className="ri-loader-4-line animate-spin align-bottom me-1"></i>
                  ) : (
                    <i className="ri-refund-2-line align-bottom me-1"></i>
                  )}
                  Issue Refund
                </Button>
              )}
            </>
          )}
        </div>
      );
    }

    if (invoice.paymentProcessing) {
      return (
        <Button color="warning" className="btn btn-warning" disabled>
          <i className="ri-loader-2-line animate-spin align-bottom me-1"></i>
          Processing
        </Button>
      );
    }

    return (
      <div className="d-flex align-items-center">
        <i
          id="payButton"
          className="ri-information-fill cursor-pointer text-muted me-2 fs-4"
        ></i>
        <Button
          onClick={isCurrentMonthPage ? payInvoiceOfMonth : payInvoiceById}
          color="primary"
          className="btn btn-primary"
          disabled={loadingPay}
        >
          <i
            className="
          ri-bank-card-line
          align-bottom me-1"
          ></i>
          Pay
        </Button>
        <UncontrolledPopover
          trigger="hover"
          placement="right"
          target="payButton"
        >
          <PopoverBody className="shadow-lg ">
            Pay anytime on or before the due date. Once payment is submitted for
            the month, any additional employees that are added will appear on
            next month's invoice.
          </PopoverBody>
        </UncontrolledPopover>
      </div>
    );
  };

  const renderCollapse = (item, index) => {
    const subItems = item.commissions;
    return (
      <td
        className={
          isCollapseOpen === index
            ? "bg-light border-bottom border-2 "
            : "bg-white"
        }
        key={index}
        colSpan="5"
      >
        <Collapse isOpen={isCollapseOpen === index}>
          <Table className="table-borderless  text-center table-nowrap align-middle mb-0">
            <thead>
              <tr className="table-active">
                <th className="text-start">Description</th>
                <th className="text-end">Amount</th>
              </tr>
            </thead>
            <tbody>
              {subItems?.map((subItem, subIndex) => (
                <tr className="text-start" key={subIndex}>
                  <td>{subItem.description}</td>
                  <td className="text-end">
                    ${parsedInvoiceAmount(subItem.amount)}
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </Collapse>
      </td>
    );
  };

  const renderInvoiceDetailsTable = (invoice) => {
    const itemsToMap = invoice?.items;
    return (
      <>
        {itemsToMap?.map((item, index) => {
          const itemCommissions = item.commissions;
          const isFamilyTier = item.metadata?.employee?.tier === 'F' ||
            item.metadata?.employee?.isFamily === true ||
            item.metadata?.pricingTier === 'family';

          return (
            <React.Fragment key={item.id}>
              <tr>
                <th scope="row">{index + 1}</th>
                <td className="text-start">
                  <span className="fw-medium">{item.description}</span>
                  <p className="text-muted mb-0">
                    {capitalizeFirstLetter(item.type)}
                  </p>
                </td>
                <td className="text-center">
                  <span className={`badge rounded-pill ${isFamilyTier ? 'bg-info' : 'bg-secondary'}`}>
                    {isFamilyTier ? 'F' : 'E'}
                  </span>
                </td>
                <td className="text-end">
                  ${parsedInvoiceAmount(item.total || item.amount)}
                </td>
              </tr>
              {/* {itemCommissions?.length > 0 && renderCollapse(item, index)} */}
            </React.Fragment>
          );
        })}
      </>
    );
  };

  if (loading || !invoice) {
    return (
      <div className="page-content">
        <div
          className="text-center d-flex justify-content-center align-items-center"
          style={{ minHeight: "150px", height: "150px" }}
        >
          <div className="spinner-border spinner-border-lg mr-3"></div>
        </div>
      </div>
    );
  }

  const renderCommissionsTable = () => {
    if (
      !loading &&
      invoice?.commissions &&
      invoice?.commissions.length > 0 &&
      currentUser.role === DASHBOARD_USER_ROLES.ADMIN
    ) {
      return (
        <>
          <h3>Commissions</h3>
          {commissions.map((commission, index) => {
            const { companyName, firstName, lastName, role } =
              commission.dashboardUserInfo || {};

            const displayName = companyName
              ? companyName
              : firstName && lastName
                ? `${firstName} ${lastName}`
                : firstName || lastName || "";

            return (
              <div key={index}>
                <hr />
                <h5>{displayName} ({singularRoleLabels[role]
                })</h5>
                <CommissionDetailTable commission={commission} />
              </div>
            );
          })}
        </>
      );
    }
    return null;
  };

  return (
    <div className="page-content">
      <Container fluid>
        <div className="d-flex justify-content-between">
          <div>
            <ButtonBack />
          </div>
          <div className="ms-auto">{renderButtonPayNow(invoice)}</div>
        </div>

        <Row className="justify-content-center mt-3  position-relative">
          {loadingPay && <Loading />}
          <Col xxl={12}>
            {/* <div className="col-12 d-flex align-items-center">
              <label
                htmlFor="like-input"
                className="form-label fw-semibold fs-3"
              >
                Invoice
              </label>
            </div> */}
            <Card id="demo" className="border">
              <Row>
                <Col lg={12}>
                  <CardHeader className="border-bottom-dashed p-4">
                    <div className="d-flex flex-column">
                      <div className="d-flex justify-content-between align-items-center">
                        <PrimaryLogo />

                        <div
                          onClick={downloadInvoice}
                          className="btn btn-primary"
                        >
                          <i className="ri-download-2-line align-bottom me-1"></i>{" "}
                          Download Invoice
                        </div>
                      </div>
                      <div className="d-flex justify-content-between align-items-center mt-5">
                        <div className="bg rounded-4 p-3 col-3" style={{ background: "var(--thead-bg)" }}>
                          <h6 className="text-dark text-uppercase fw-semibold">
                            Address
                          </h6>
                          <p className="text-muted mb-1" id="address-details">
                            4625 Lange Avenue NE, United States
                          </p>
                          <p className="text-muted mb-0" id="zip-code">
                            <span>St. Michael, MN 55376</span>
                          </p>
                        </div>
                        <div className="text-end text-primary rounded-4 p-3" style={{ background: "var(--thead-bg)" }}>
                          <h6 className="mb-2 d-flex align-items-center">
                            <i className="ri-mail-line me-2 fs-4 text-primary"></i>
                            <span
                              id="email"
                              className="text-primary fw-semibold fs-6"
                            >
                              support@baxrx.com
                            </span>
                          </h6>
                          <h6 className="mb-0 d-flex align-items-center">
                            <i className="ri-global-line me-2 fs-4 text-primary"></i>
                            <Link
                              to="https://baxrx.com"
                              target="_blank"
                              className="link-primary text-primary fw-semibold fs-6"
                              id="website"
                            >
                              https://baxrx.com
                            </Link>
                          </h6>
                        </div>
                      </div>
                    </div>
                  </CardHeader>
                </Col>
                <Col lg={12}>
                  <CardBody className="p-4">
                    <Row className="g-3">
                      {invoice.id && (
                        <Col
                          lg={3}
                          xs={6}
                          className="border rounded-4 p-3 mx-2"
                        >
                          <p className="mb-2 text-uppercase fw-normal">
                            Invoice ID
                          </p>
                          <h5 className="fs-14 mb-0 text-muted">
                            <span id="invoice-no">{invoice.id}</span>
                          </h5>
                        </Col>
                      )}
                      <Col lg={3} xs={6} className="border rounded-4 p-3 me-2">
                        <p className="text-dark mb-2 text-uppercase fw-normal">
                          Date
                        </p>
                        <h5 className="fs-14 mb-0 fw-normal">
                          <span id="invoice-date">
                            {formatDateForInvoice(invoice.date)}
                          </span>{" "}
                          <small className="text-muted" id="invoice-time">
                            {formatTimeForInvoice(invoice.date)}
                          </small>
                        </h5>
                      </Col>
                      <Col lg={3} xs={6} className="border rounded-4 p-3">
                        <p className="text-dark mb-2 text-uppercase fw-normal">
                          Payment Status
                        </p>
                        {invoice.refunded && !invoice.partiallyRefunded ? (
                          <span className="badge fs-11 badge-soft-secondary">
                            Fully Refunded
                          </span>
                        ) : invoice.partiallyRefunded ? (
                          <span className="badge fs-11 badge-soft-warning">
                            Partially Refunded
                          </span>
                        ) : (
                          <InvoicePaymentStatusBadge invoice={invoice} />
                        )}

                        {invoice.failed ? (
                          <span className="badge fs-11 badge-soft-danger ms-1">
                            Payment Failed
                          </span>
                        ) : null}
                      </Col>
                      {(invoice.refunded || invoice.partiallyRefunded) && (
                        <Col lg={3} xs={6} className="border rounded-4 p-3 border-warning">
                          <p className="text-dark mb-2 text-uppercase fw-normal">
                            Total Refunded
                          </p>
                          <h5 className="fs-14 mb-0 fw-normal">
                            ${parsedInvoiceAmount(invoice.totalRefundedAmount || 0)}
                            {invoice.refundedAt && (
                              <small className="text-muted ms-2">
                                Last: {moment(invoice.refundedAt).format("MM/DD/YYYY")}
                              </small>
                            )}
                          </h5>
                        </Col>
                      )}
                      <Col lg={3} xs={6} className="border rounded-4 p-3 ms-2">
                        <p className="text-dark mb-2 text-uppercase fw-normal">
                          Total Amount
                        </p>
                        <h5 className="fs-14 mb-0">
                          $
                          <span id="total-amount">
                            {parsedInvoiceAmount(invoice.totalAmount)}
                          </span>
                        </h5>
                      </Col>
                    </Row>
                  </CardBody>
                </Col>
                {renderBillingAddress(invoice)}
                <Col lg={12}>
                  <CardBody className="p-4">
                    <div className="table-responsive">
                      <Table className="border text-center table-nowrap align-middle mb-0">
                        <thead>
                          <tr className=" cursor-default bg" style={{ background: "var(--thead-bg)" }}>
                            <th scope="col" style={{ width: "50px" }}>
                              #
                            </th>
                            <th scope="col" className="text-start">
                              Invoice Details
                            </th>
                            <th scope="col" className="text-center" style={{ width: "80px" }}>
                              Tier
                            </th>
                            <th scope="col" className="text-end">
                              Amount
                            </th>
                          </tr>
                        </thead>
                        <tbody id="products-list ">
                          {renderInvoiceDetailsTable(invoice)}
                        </tbody>
                      </Table>
                    </div>
                    <div className="border-top border-top-dashed mt-2">
                      <Table
                        className="table table-borderless table-nowrap align-middle mb-0 ms-auto"
                        style={{ width: "250px" }}
                      >
                        <tbody>
                          <tr className="border-top border-top-dashed fs-15">
                            <th scope="row">Total Amount</th>
                            <th className="text-end">
                              ${parsedInvoiceAmount(invoice.totalAmount)}
                            </th>
                          </tr>
                          {(invoice.totalRefundedAmount > 0) && (
                            <>
                              <tr className="text-warning">
                                <td>Total Refunded</td>
                                <td className="text-end">
                                  -${parsedInvoiceAmount(invoice.totalRefundedAmount)}
                                </td>
                              </tr>
                              <tr className="border-top border-top-dashed fs-15">
                                <th scope="row">Net Amount</th>
                                <th className="text-end">
                                  ${parsedInvoiceAmount(invoice.totalAmount - invoice.totalRefundedAmount)}
                                </th>
                              </tr>
                            </>
                          )}
                        </tbody>
                      </Table>
                    </div>

                    {/* Refund History Section */}
                    {isAdmin && invoice.refundHistory && invoice.refundHistory.length > 0 && (
                      <div className="mt-4 border rounded-3 p-3">
                        <h6 className="text-muted text-uppercase fw-semibold mb-3">
                          <i className="ri-history-line me-2"></i>
                          Refund History
                        </h6>
                        <Table className="table table-sm table-borderless mb-0">
                          <thead>
                            <tr className="table-light">
                              <th>Date</th>
                              <th className="text-end">Amount</th>
                            </tr>
                          </thead>
                          <tbody>
                            {invoice.refundHistory.map((refund, index) => (
                              <tr key={index}>
                                <td>
                                  {moment(refund.refundedAt).format("MM/DD/YYYY hh:mm A")}
                                </td>
                                <td className="text-end text-warning">
                                  -${parsedInvoiceAmount(refund.amount)}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </Table>
                      </div>
                    )}

                    {/* {renderPaymentMethod()}
                  {renderNotes()} */}

                    <div className="hstack gap-2 justify-content-end d-print-none mt-4">
                      {renderPayInvoiceButton && renderButtonPayNow(invoice)}
                    </div>
                    {renderCommissionsTable()}
                  </CardBody>
                </Col>
              </Row>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default InvoiceDetails;
